/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { Periods, MEASURE_LABELS, Measure, Entities, Channel } from 'cio/utils/metrics/common';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import { Grouping } from 'cio/utils/metrics/resolution';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import ActionModel, { ActionType, VariationBadge } from 'cio/models/action';
import actionInfo, {
  actionTypeName,
  deliveryStatesAndActionsPerType,
  DELIVERY_STATES_AND_ACTIONS_PER_TYPE,
} from 'cio/utils/action-info';
import { generateSortBy, SortDirection } from 'cio/utils/sort';
import { MEASURES as ANALYSIS_MEASURE_ORDER } from 'cio/utils/metrics/analysis';
import { Metric, summarizeMetrics } from 'cio/utils/metrics/models/metric';
import DS from 'ember-data';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import flattenArray from 'cio/utils/flatten-array';
import SettingsService from 'cio/services/settings';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';

interface CampaignMetricsPageMessageMetricsCardArgs {
  campaign: CampaignModel;

  channelMetrics: Partial<Record<Channel, Metric>>;
  actionMetrics: Record<string, Metric>;
  isLoadingMetrics: boolean;

  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  unit: 'count' | 'percentage';

  channels: Channel[];
  onChannelsChange: (channels: Channel[]) => unknown;

  sortBy: 'action_name' | Measure;
  sortDirection: SortDirection;
  onSortChange: (sortBy: 'action_name' | Measure, sortDirection: SortDirection) => unknown;

  columns: Measure[];
  onColumnsChange: (columns: Measure[], track?: boolean) => unknown;

  afterMetricsLoaded?: () => unknown;

  onTrackCopyLink: () => unknown;
}

const ROW_HEIGHT = 73;

const DELIVERY_METRICS_MEASURES = ANALYSIS_MEASURE_ORDER.filter((m) => m.includes('delivery_metrics')).map(
  (m) => m.split('.')[1],
);

interface BaseActionMetricRow {
  action?: ActionModel;
  actionId?: string;
  name?: string;
  icon?: string;
  metric: Metric;
  isSplit: boolean;
  isVariation: boolean;
  isMultiLanguage: boolean;
  isTotal: boolean;
}

export interface NonSplitActionMetricRow extends BaseActionMetricRow {
  action: ActionModel;
  actionId: string;
  name: string;
  icon: string;
  isSplit: false;
  isVariation: false;
  isMultiLanguage: false;
  isTotal: false;
}

export interface VariationActionMetricRow extends BaseActionMetricRow {
  action: ActionModel;
  actionId: string;
  name: string;
  icon: string;
  isSplit: false;
  isVariation: true;
  isMultiLanguage: false;
  isTotal: false;
  badge: VariationBadge;
}

export interface SplitActionMetricRow extends BaseActionMetricRow {
  action: ActionModel;
  actionId: string;
  name: string;
  icon: string;
  isSplit: true;
  isVariation: false;
  isMultiLanguage: false;
  isTotal: false;
  variations: VariationActionMetricRow[];
}

export interface MultiLanguageActionMetricRow extends BaseActionMetricRow {
  action: ActionModel;
  actionId: string;
  name: string;
  icon: string;
  isSplit: false;
  isVariation: false;
  isMultiLanguage: true;
  isTotal: false;
  variations: LanguageActionMetricRow[];
}

export interface LanguageActionMetricRow extends BaseActionMetricRow {
  action: ActionModel;
  actionId: string;
  name: string;
  isSplit: false;
  isVariation: false;
  isMultiLanguage: false;
  isTotal: false;
  language: string;
}

export interface TotalMetricRow extends BaseActionMetricRow {
  metric: Metric;
  isSplit: false;
  isVariation: false;
  isMultiLanguage: false;
  isTotal: true;
}

export type ActionMetricRow =
  | NonSplitActionMetricRow
  | VariationActionMetricRow
  | SplitActionMetricRow
  | MultiLanguageActionMetricRow
  | LanguageActionMetricRow
  | TotalMetricRow;

function sortActionMetricRowsByRate(sortDirection: SortDirection, measure: Measure) {
  let sortFirst = sortDirection === SortDirection.Ascending ? 1 : -1;
  let sortLast = sortDirection === SortDirection.Ascending ? -1 : 1;

  return function (a: ActionMetricRow, b: ActionMetricRow) {
    if (measure === 'converted') {
      if (a.action?.canConvert && !b.action?.canConvert) {
        return sortFirst;
      }

      if (!a.action?.canConvert && b.action?.canConvert) {
        return sortLast;
      }
    }

    if (a.action != null && b.action != null) {
      let aType = a.action.type === 'split_randomized_action' ? 'email_action' : a.action.type;
      let bType = b.action.type === 'split_randomized_action' ? 'email_action' : b.action.type;
      let aCanMeasure = (DELIVERY_STATES_AND_ACTIONS_PER_TYPE[aType] ?? []).includes(measure);
      let bCanMeasure = (DELIVERY_STATES_AND_ACTIONS_PER_TYPE[bType] ?? []).includes(measure);

      if (aCanMeasure && !bCanMeasure) {
        return sortFirst;
      }

      if (!aCanMeasure && bCanMeasure) {
        return sortLast;
      }
    }

    let rateA = a.metric[measure]?.rate;
    let rateB = b.metric[measure]?.rate;

    if (rateA == null || rateB == null) {
      // Sort non-null values higher
      if (rateA != null && rateB == null) {
        return sortFirst;
      }

      if (rateA == null && rateB != null) {
        return sortLast;
      }

      if (rateA == null && rateB == null) {
        return ((a.metric[measure]?.total ?? 0) - (b.metric[measure]?.total ?? 0)) * sortFirst;
      }

      return 0;
    }

    if (rateA > rateB) {
      return sortFirst;
    }

    if (rateA < rateB) {
      return sortLast;
    }

    return 0;
  };
}

export default class CampaignMetricsPageMessageMetricsCard extends Component<CampaignMetricsPageMessageMetricsCardArgs> {
  @service store!: DS.Store;
  @service metrics!: MetricsService;
  @service environment!: EnvironmentService;
  @service settings!: SettingsService;

  constructor(owner: unknown, args: CampaignMetricsPageMessageMetricsCardArgs) {
    super(owner, args);

    this.allSelectedColumns = [...this.args.columns];
  }

  // This keeps track of all columns that should be selected, even if they're
  // not available with the current channels selection. When switching channels,
  // we'll reference this to see what was selected before to update the current
  // selection.
  @tracked allSelectedColumns: Measure[] = [];

  get channelActionTypes() {
    return this.args.channels.map((c) => `${c}_action`);
  }

  get channelDropdownGroups() {
    return [
      {
        label: 'All channels',
        value: 'all_channels',
        items: this.environment.availableChannelsForCampaign(this.args.campaign).map((typeName) => {
          let info = actionInfo(`${typeName}_action` as ActionType);

          return {
            label: info.name,
            value: typeName,
            icon: info.icon,
          };
        }),
      },
    ];
  }

  @action
  onChannelsSelect(channels: Channel[]) {
    this.args.onChannelsChange(channels);

    let newColumns = [...this.allSelectedColumns]
      .filter((c) => this.deliveryMetricsAvailableForChannels.includes(c))
      .sort((a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b));
    this.args.onColumnsChange(newColumns, false);

    this.prepareTotalRow();
  }

  get deliveryMetricsAvailableForChannels() {
    let availableMeasures = flattenArray(
      this.args.channels.map(
        (channel) => deliveryStatesAndActionsPerType(true)[`${channel}_action` as ActionType] ?? [],
      ),
    );

    if (this.args.channels.includes('email')) {
      availableMeasures.push('delivered');
    }

    return availableMeasures.compact().uniq();
  }

  @action
  onSortChange(sortBy: 'action_name' | Measure, sortDirection: SortDirection) {
    this.args.onSortChange(sortBy, sortDirection);
  }

  allMeasures = [
    'sent',
    'delivered',
    'opened',
    'clicked',
    'clicked_to_opened',
    'converted',
    'unsubscribed',
    'topic_unsubscribed',
    'spammed',
    'failed',
    'bounced',
    'suppressed',
  ];
  allMeasureOptions = this.allMeasures.map((measure: Measure) => ({
    label: MEASURE_LABELS[measure],
    value: measure,
  }));

  get availableMeasures() {
    return this.allMeasures.filter((m) => this.deliveryMetricsAvailableForChannels.includes(m));
  }
  get availableMeasureOptions() {
    return this.allMeasureOptions.filter((m) => this.deliveryMetricsAvailableForChannels.includes(m.value));
  }
  get dropdownMeasureOptions() {
    return [
      {
        displayOnly: true,
        value: 'metrics',
        items: this.availableMeasureOptions,
      },
    ];
  }

  @action
  onSelectedMeasuresChanged(measures: Measure[]) {
    let previousColumns = [...this.args.columns];
    let newColumns = [...measures].sort(
      (a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b),
    );
    // We set measures like this to maintain the default sort order
    this.args.onColumnsChange(newColumns);

    let removedColumns = previousColumns.filter((m) => !newColumns.includes(m));
    this.allSelectedColumns = [...this.allSelectedColumns.filter((m) => !removedColumns.includes(m)), ...newColumns]
      .uniq()
      .sort((a, b) => DELIVERY_METRICS_MEASURES.indexOf(a) - DELIVERY_METRICS_MEASURES.indexOf(b));
  }

  @tracked availableActions: ActionModel[] = [];
  @tracked actionMetricRows: ActionMetricRow[] = [];
  @tracked totalMetricRow: ActionMetricRow | null = null;

  get filteredActionMetricRows() {
    return this.actionMetricRows.filter((r) => {
      if (r.action?.type == null) {
        return false;
      }

      let actionType = r.action.type;

      if (actionType === 'split_randomized_action') {
        actionType = 'email_action';
      } else if (r.isMultiLanguage && r.variations.length > 0) {
        actionType = r.variations[0].action.type;
      }

      return this.channelActionTypes.includes(actionType);
    });
  }

  get sortedActionMetricRows() {
    let sortFunction;

    if (this.args.sortBy === 'action_name') {
      sortFunction = generateSortBy(this.args.sortDirection, 'name');
    } else {
      let denominator = findDenominator(this.args.sortBy, MeasureType.DeliveryMetrics);
      let sortByRate = this.args.sortBy !== 'sent' && denominator != null;

      if (!sortByRate) {
        sortFunction = generateSortBy(
          this.args.sortDirection,
          `metric.${this.args.sortBy}.${sortByRate ? 'rate' : 'total'}`,
        );
      } else {
        sortFunction = sortActionMetricRowsByRate(this.args.sortDirection, this.args.sortBy);
      }
    }

    return [...this.filteredActionMetricRows].sort(sortFunction);
  }

  get placeholderHeight() {
    return this.args.campaign.messagingActions.reduce<number>((acc, a) => {
      if (a.isSplitTest && this.args.channels.includes('email')) {
        return acc + ROW_HEIGHT * 3;
      }

      if (this.args.channels.includes(actionTypeName(a.type) as Channel)) {
        return acc + ROW_HEIGHT;
      }

      return acc;
    }, 0);
  }

  @action
  isLoadingChanged() {
    if (this.args.isLoadingMetrics) {
      return;
    }

    this.args.campaign.parseTree();

    this.actionMetricRows = this.args.campaign.workflowActions
      .filter((action) => action.isMessagingType || action.isSplitTest || action.isMultiLangBranch)
      .reduce<ActionMetricRow[]>((rows, action) => {
        if (action.isSplitTest) {
          rows = this.appendSplitTestActionRowMetric(action, rows);
        } else if (action.isMultiLangBranch) {
          rows = this.appendMultiLanguageBranchActionRowMetric(action, rows);
        } else {
          rows = this.appendDefaultMetricRow(action, rows);
        }

        return rows;
      }, []);

    this.prepareTotalRow();
  }

  appendDefaultMetricRow(action: ActionModel, metricRows: ActionMetricRow[]) {
    if (isMultiLangBranchChild(action)) {
      return metricRows;
    }

    return [
      ...metricRows,
      <ActionMetricRow>{
        action,
        actionId: action.id,
        name: action.name,
        icon: action.info.icon,
        metric: this.args.actionMetrics[action.id],
        isSplit: false,
        isVariation: false,
        isMultiLanguage: false,
        isTotal: false,
      },
    ];
  }

  appendSplitTestActionRowMetric(action: ActionModel, metricRows: ActionMetricRow[]) {
    let childRows: VariationActionMetricRow[] = action.splitActions.map((a) => ({
      action: a.action,
      actionId: a.action.id,
      name: a.action.name,
      icon: a.action.info.icon,
      metric: this.args.actionMetrics[a.action.id],
      badge: a.badge,
      isSplit: false,
      isVariation: true,
      isMultiLanguage: false,
      isTotal: false,
    }));

    return [
      ...metricRows,
      <SplitActionMetricRow>{
        action,
        actionId: action.id,
        name: action.splitTestName,
        icon: action.info.icon,
        metric: this.args.actionMetrics[action.id],
        isSplit: true,
        isVariation: false,
        isMultiLanguage: false,
        isTotal: false,
        variations: childRows,
      },
    ];
  }

  appendMultiLanguageBranchActionRowMetric(action: ActionModel, metricRows: ActionMetricRow[]) {
    let childActions = this.args.campaign.getConsolidatedBranchChildActions(action.id);
    let defaultAction = childActions[childActions.length - 1];

    if (childActions.length === 0 || defaultAction == null) {
      return metricRows;
    }

    let childRows = childActions
      .map((a) => {
        let language = this.args.campaign.getConsolidatedBranchChildActionLanguage(a.id);
        if (language === '') {
          language = 'Default';
        }

        return <LanguageActionMetricRow>{
          action: a,
          actionId: defaultAction.id,
          name: language,
          metric: this.args.actionMetrics[a.id],
          isSplit: false,
          isVariation: false,
          isMultiLanguage: false,
          isTotal: false,
          language,
        };
      })
      .sort((a, b) => {
        if (a.language === 'Default') {
          return -2;
        } else if (b.language === 'Default') {
          return 2;
        }

        return a.language.localeCompare(b.language);
      });

    return [
      ...metricRows,
      <MultiLanguageActionMetricRow>{
        action,
        actionId: defaultAction.id,
        name: defaultAction.name!,
        icon: childRows[0].action.info.icon,
        metric: this.args.actionMetrics[action.id],
        isSplit: false,
        isVariation: false,
        isMultiLanguage: true,
        isTotal: false,
        variations: childRows,
      },
    ];
  }

  @action
  prepareTotalRow() {
    let metricsForChannels = this.args.channels.map((c) => this.args.channelMetrics[c]).compact();

    this.totalMetricRow = {
      metric: summarizeMetrics(this.args.campaign.id, Entities.CAMPAIGN, null, metricsForChannels),
      isSplit: false,
      isVariation: false,
      isMultiLanguage: false,
      isTotal: true,
    };
  }
}
